import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { Link, router } from '@inertiajs/react'
import { Check, ChevronDown, FileCheck, FileText, MoreHorizontal, Pencil, Search, Ship as ShipIcon, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { formatCurrency, formatDate, formatFileSize } from '@/lib/formatters'
import { BOOKING_STATUSES, shortContainerType } from '@/lib/constants'
import { capitalize } from '@/lib/utils'
import type { BookingDocument, BookingFull, BookingMovePreview, DocumentType, ReviewOutcome, TransferShipmentContext } from './types'
import { compareDocumentsByRecency, documentTypeLabel, documentTypePillClassName, formatBookingMoveOption, overviewBookingDocumentRows } from './helpers'
import DocumentUploadDialog from './DocumentUploadDialog'
import { LatestMetadataBadge, ReviewOutcomeSelect } from './DocumentReviewControls'
import { StageRail } from './StageRail'

export default function BookingCard({ booking, allBookings, pathFor, onEdit, onSelectDocuments, shipmentId }: {
  booking: BookingFull
  allBookings: BookingFull[]
  pathFor: (path: string) => string
  onEdit: () => void
  onSelectDocuments: (bookingId?: string | null) => void
  shipmentId: string
}) {
  const isConfirmed = ['confirmed', 'si_submitted', 'bl_released'].includes(booking.status || '')
  const quoteExpired = booking.quote_snapshot?.expired
  const quoteNeedsReview = booking.quote_snapshot?.manual_review_required || booking.quote_snapshot?.review_reason
  const quoteSourceLabel = rateSourceLabel(booking.quote_snapshot?.rate_source)
  const quoteCarrierReference = booking.quote_snapshot ? carrierSnapshotReference(booking.quote_snapshot) : null
  const siDocs = booking.documents.filter(d => d.document_type === 'si').sort(compareDocumentsByRecency)
  const blDocs = booking.documents.filter(d => d.document_type === 'bl').sort(compareDocumentsByRecency)
  const otherDocs = booking.documents.filter(d => d.document_type === 'other').sort(compareDocumentsByRecency)
  const isTransferred = booking.status === 'transferred'
  const canMoveBooking = ['draft', 'quoted', 'accepted', 'confirmed', 'si_submitted', 'bl_released'].includes(booking.status || '')
  
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showTransferDialog, setShowTransferDialog] = useState(false)
  const [transferTargetSearch, setTransferTargetSearch] = useState('')
  const [transferTargetShipment, setTransferTargetShipment] = useState<TransferShipmentContext | null>(null)
  const [transferSearchResults, setTransferSearchResults] = useState<TransferShipmentContext[]>([])
  const [transferSearchLoading, setTransferSearchLoading] = useState(false)
  const [transferLookupError, setTransferLookupError] = useState<string | null>(null)
  const [transferIncludeDocuments, setTransferIncludeDocuments] = useState<boolean | null>(null)
  const [transferAcknowledged, setTransferAcknowledged] = useState(false)
  const [transferPreview, setTransferPreview] = useState<BookingMovePreview | null>(null)
  const [transferPreviewLoading, setTransferPreviewLoading] = useState(false)
  const [transferSubmitError, setTransferSubmitError] = useState<string | null>(null)
  const [isTransferring, setIsTransferring] = useState(false)

  // Check if there are multiple confirmed bookings on this shipment
  const confirmedBookingsCount = allBookings.filter(b => b.status === 'confirmed').length
  const hasMultipleConfirmedBookings = confirmedBookingsCount > 1

  // State for booking card document move menu
  const [menuDocId, setMenuDocId] = useState<string | null>(null)
  const [menuDocType, setMenuDocType] = useState<DocumentType | null>(null)
  const [movingDocId, setMovingDocId] = useState<string | null>(null)
  const [changingDocTypeId, setChangingDocTypeId] = useState<string | null>(null)
  const [docToDelete, setDocToDelete] = useState<{id: string, type: DocumentType} | null>(null)
  const [showStatusMenu, setShowStatusMenu] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const statusMenuRef = useRef<HTMLDivElement>(null)

  function handleStatusChange(status: string) {
    setShowStatusMenu(false)
    if (status === booking.status) return

    router.patch(pathFor(`/bookings/${booking.id}`), {
      booking: { status },
    }, {
      preserveState: true,
      onError: () => toast.error('Failed to update booking status'),
    })
  }

  function handleKeyboardActivation(event: KeyboardEvent<HTMLDivElement>, action: () => void) {
    if (event.key !== 'Enter' && event.key !== ' ') return

    event.preventDefault()
    action()
  }

  function handleDelete() {
    setShowDeleteDialog(false)
    router.delete(pathFor(`/bookings/${booking.id}`), {
      onError: () => {
        toast.error('Failed to cancel booking')
      },
    })
  }

  function openTransferDialog() {
    setTransferTargetSearch('')
    setTransferTargetShipment(null)
    setTransferSearchResults([])
    setTransferLookupError(null)
    setTransferIncludeDocuments(null)
    setTransferAcknowledged(false)
    setTransferPreview(null)
    setTransferSubmitError(null)
    setShowTransferDialog(true)
  }

  function csrfToken(): string {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
  }

  async function fetchTransferPreview(target: TransferShipmentContext, includeDocuments = transferIncludeDocuments) {
    setTransferPreviewLoading(true)
    setTransferSubmitError(null)
    try {
      const response = await fetch(pathFor(`/bookings/${booking.id}/transfer/preview`), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken(),
        },
        body: JSON.stringify({
          target_shipment_id: target.id,
          include_documents: includeDocuments,
        }),
      })
      const payload = await response.json() as BookingMovePreview
      setTransferPreview(payload)
      setTransferAcknowledged(false)
      return payload
    } catch {
      const payload: BookingMovePreview = {
        warnings: [],
        hard_blocks: [],
        document_policy: 'no_documents',
        include_documents_required: false,
        comparison_rows: [],
        error: 'Could not preview booking move.',
      }
      setTransferPreview(payload)
      return payload
    } finally {
      setTransferPreviewLoading(false)
    }
  }

  function selectTransferTarget(shipment: TransferShipmentContext) {
    setTransferTargetShipment(shipment)
    setTransferTargetSearch(shipment.internal_reference)
    setTransferSearchResults([])
    setTransferLookupError(null)
    setTransferIncludeDocuments(null)
    setTransferSubmitError(null)
    fetchTransferPreview(shipment, null)
  }

  async function handleTransfer() {
    if (!transferTargetShipment) {
      toast.error('Select a target shipment')
      return
    }
    if (transferPreview?.include_documents_required && transferIncludeDocuments === null) {
      toast.error('Choose whether to include linked documents')
      return
    }
    if ((transferPreview?.warnings.length || 0) > 0 && !transferAcknowledged) {
      toast.error('Review and acknowledge warnings before moving')
      return
    }
    const blockingHardBlocks = (transferPreview?.hard_blocks || []).filter((block) => block.field !== 'include_documents')
    if (blockingHardBlocks.length > 0) {
      toast.error(blockingHardBlocks[0].message)
      return
    }

    setIsTransferring(true)
    try {
      const response = await fetch(pathFor(`/bookings/${booking.id}/transfer`), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken(),
        },
        body: JSON.stringify({
          target_shipment_id: transferTargetShipment.id,
          include_documents: transferIncludeDocuments,
          acknowledge_warnings: transferAcknowledged,
        }),
      })
      const payload = await response.json()
      if (response.ok) {
        setShowTransferDialog(false)
        router.visit(payload.redirect_url)
        return
      }

      setTransferPreview(payload as BookingMovePreview)
      setTransferSubmitError(payload.error || 'Failed to move booking')
      setTransferAcknowledged(false)
    } catch {
      setTransferSubmitError('Failed to move booking')
      toast.error('Failed to move booking')
    } finally {
      setIsTransferring(false)
    }
  }

  const transferBlockingHardBlocks = (transferPreview?.hard_blocks || []).filter((block) => block.field !== 'include_documents')
  const transferDocumentDecisionMissing = !!transferPreview?.include_documents_required && transferIncludeDocuments === null
  const transferCanSubmit = !!transferTargetShipment &&
    transferBlockingHardBlocks.length === 0 &&
    !transferDocumentDecisionMissing &&
    ((transferPreview?.warnings.length || 0) === 0 || transferAcknowledged)
  const transferWarningFields = new Set(transferPreview?.warnings.map((warning) => warning.field) || [])

  useEffect(() => {
    if (!showTransferDialog) return

    const query = transferTargetSearch.trim()
    if (query.length < 2 || transferTargetShipment?.internal_reference === query) {
      setTransferSearchResults([])
      return
    }

    let cancelled = false
    const timer = window.setTimeout(async () => {
      setTransferSearchLoading(true)
      setTransferLookupError(null)
      try {
        const response = await fetch(pathFor(`/shipments/search.json?q=${encodeURIComponent(query)}`), {
          headers: { Accept: 'application/json' },
        })
        if (!response.ok) throw new Error('lookup failed')
        const results = await response.json() as TransferShipmentContext[]
        if (!cancelled) setTransferSearchResults(results)
      } catch {
        if (!cancelled) setTransferLookupError('Could not search shipments')
      } finally {
        if (!cancelled) setTransferSearchLoading(false)
      }
    }, 250)

    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [showTransferDialog, transferTargetSearch, transferTargetShipment?.internal_reference, pathFor])

  // Handle opening the document action menu (toggle)
  function openBookingCardMenu(docType: DocumentType, docId: string) {
    if (menuDocId === docId && menuDocType === docType) {
      // Close if already open
      setMenuDocId(null)
      setMenuDocType(null)
      setMovingDocId(null)
      setChangingDocTypeId(null)
    } else {
      // Open new menu
      setMenuDocType(docType)
      setMenuDocId(docId)
      setMovingDocId(null)
      setChangingDocTypeId(null)
    }
  }

  // Handle moving a document to a different booking
  function handleMoveDocument(docId: string, targetBookingId: string | null) {
    const formData = new FormData()
    if (targetBookingId) {
      formData.append('booking_id', targetBookingId)
    } else {
      formData.append('booking_id', '')
    }
    
    router.patch(pathFor(`/shipment_documents/${docId}`), formData, {
      preserveState: true,
      onSuccess: () => {
        setMenuDocId(null)
        setMovingDocId(null)
        setChangingDocTypeId(null)
      },
      onError: () => {
        toast.error('Failed to move document')
      }
    })
  }

  function handleUpdateDocumentType(docId: string, documentType: DocumentType) {
    router.patch(pathFor(`/shipment_documents/${docId}`), {
      document_type: documentType
    }, {
      preserveState: true,
      onSuccess: () => {
        setMenuDocId(null)
        setMenuDocType(null)
        setMovingDocId(null)
        setChangingDocTypeId(null)
      },
      onError: () => {
        toast.error('Failed to update document type')
      }
    })
  }

  function handleUpdateReviewOutcome(docId: string, reviewOutcome: ReviewOutcome) {
    router.patch(pathFor(`/shipment_documents/${docId}`), {
      review_outcome: reviewOutcome
    }, {
      preserveState: true,
      onSuccess: () => {
        setMenuDocId(null)
        setMenuDocType(null)
        setMovingDocId(null)
        setChangingDocTypeId(null)
      },
      onError: () => {
        toast.error('Failed to update document review')
      }
    })
  }

  // Handle delete document with modal confirmation
  function handleDeleteDocument() {
    if (!docToDelete) return
    
    router.delete(pathFor(`/shipment_documents/${docToDelete.id}`), {
      preserveState: true,
      onSuccess: () => {
        setDocToDelete(null)
        setMenuDocId(null)
      },
      onError: () => {
        toast.error('Failed to delete document')
        setDocToDelete(null)
      }
    })
  }

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuDocId(null)
        setMenuDocType(null)
        setChangingDocTypeId(null)
      }
    }
    
    if (menuDocId) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [menuDocId])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (statusMenuRef.current && !statusMenuRef.current.contains(event.target as Node)) {
        setShowStatusMenu(false)
      }
    }

    if (showStatusMenu) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showStatusMenu])

  const BOOKING_STATUS_BADGE: Record<string, { className: string; label: string }> = {
    draft: { className: 'bg-gray-100 text-gray-500', label: 'Draft' },
    quoted: { className: 'bg-blue-50 text-blue-600', label: 'Quoted' },
    accepted: { className: 'bg-amber-100 text-amber-700', label: 'Accepted by client' },
    confirmed: { className: 'bg-green-100 text-green-600', label: 'Confirmed by carrier' },
    si_submitted: { className: 'bg-purple-100 text-purple-600', label: 'SI Submitted' },
    bl_released: { className: 'bg-emerald-100 text-emerald-600', label: 'BL Released' },
    cancelled: { className: 'bg-red-100 text-red-700', label: 'Cancelled' },
    transferred: { className: 'bg-gray-100 text-gray-700', label: 'Transferred' },
  }
  const statusBadge = BOOKING_STATUS_BADGE[booking.status || ''] || { className: 'bg-gray-100 text-gray-500', label: capitalize(booking.status || '') }
  const nextWorkItems = bookingNextWorkItems(booking, siDocs, blDocs, {
    isConfirmed,
    quoteExpired: !!quoteExpired,
    quoteNeedsReview: !!quoteNeedsReview,
  })
  const hasPendingWork = nextWorkItems.some((item) => !['Cancelled', 'Transferred', 'No action needed'].includes(item.label))
  const bookingDocumentRows = overviewBookingDocumentRows(siDocs, blDocs, otherDocs)
  const hiddenDocumentCount = Math.max(booking.documents.length - bookingDocumentRows.length, 0)
  const carrierLabel = booking.carrier_name || booking.carrier || 'Unknown'
  const containerSummary = booking.container_count ? `${booking.container_count} x ${shortContainerType(booking.container_type) || 'ctr'}` : '-'

  function renderBookingDocumentRow(doc: BookingDocument, docType: DocumentType) {
    const isLatest = doc.is_latest
    const Icon = docType === 'bl' ? FileCheck : FileText
    const title = documentDisplayName(doc, docType)
    const canReview = !isTransferred && doc.review_outcome !== null

    return (
      <div key={doc.id} data-booking-document-row className="grid grid-cols-[56px_minmax(0,1fr)_auto] items-center gap-3 px-3 py-2">
        <span className={`inline-flex h-5 w-12 items-center justify-center rounded-full text-[10px] font-semibold ${documentTypePillClassName(docType)}`}>
          {documentTypeLabel(docType)}
        </span>
        <div className="flex min-w-0 items-center gap-2">
          <Icon className={`h-3.5 w-3.5 shrink-0 ${isLatest ? 'text-blue-600' : 'text-gray-500'}`} />
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-1.5">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => window.open(pathFor(`/attachments/${doc.attachment_id}/download`), '_blank')}
                      className="block min-w-0 truncate text-left text-[12px] font-medium text-gray-900 hover:underline cursor-pointer"
                    >
                      {title}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Open {title}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              {isLatest && <LatestMetadataBadge />}
            </div>
            <span className="flex min-w-0 items-center gap-1 truncate text-[10px] text-gray-400">
              <span className="truncate">
                {formatDate(doc.created_at)}{doc.size ? ` · ${formatFileSize(doc.size)}` : ''}
              </span>
            </span>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {canReview && (
            <ReviewOutcomeSelect
              outcome={doc.review_outcome}
              onChange={(reviewOutcome) => handleUpdateReviewOutcome(doc.id, reviewOutcome)}
              align="right"
            />
          )}
          {!isTransferred && (
            <div className="relative" ref={menuDocId === doc.id && menuDocType === docType ? menuRef : undefined}>
              <button
                onClick={() => openBookingCardMenu(docType, doc.id)}
                className="flex h-4 w-4 items-center justify-center rounded border border-gray-200 text-gray-400 hover:border-gray-400 hover:text-gray-600 cursor-pointer"
                title="More actions"
              >
                <MoreHorizontal className="h-3 w-3" />
              </button>
              {menuDocId === doc.id && menuDocType === docType && (
              <div className="absolute right-0 z-20 mt-1 w-44 rounded-md border bg-white py-0.5 shadow-lg">
                {movingDocId === doc.id ? (
                  <div className="px-2 py-1">
                    <select
                      className="w-full rounded border px-1.5 py-0.5 text-[11px] cursor-pointer"
                      value={booking.id}
                      onChange={(e) => handleMoveDocument(doc.id, e.target.value || null)}
                      autoFocus
                      onBlur={() => {
                        setMovingDocId(null)
                        setMenuDocId(null)
                        setMenuDocType(null)
                      }}
                    >
                      <option value="">Select booking...</option>
                      {allBookings.filter(b => b.id !== booking.id && b.status !== 'transferred').map(b => (
                        <option key={b.id} value={b.id}>
                          {formatBookingMoveOption(b)}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : changingDocTypeId === doc.id ? (
                  <div className="px-2 py-1">
                    <select
                      aria-label={`Document type for ${title}`}
                      className="w-full rounded border px-1.5 py-0.5 text-[11px] cursor-pointer"
                      value={docType}
                      onChange={(e) => handleUpdateDocumentType(doc.id, e.target.value as DocumentType)}
                      autoFocus
                      onBlur={() => {
                        setChangingDocTypeId(null)
                        setMenuDocId(null)
                        setMenuDocType(null)
                      }}
                    >
                      <option value="si">SI</option>
                      <option value="bl">BL</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                ) : (
                  <>
                    <button
                      onClick={() => setMovingDocId(doc.id)}
                      className="w-full px-3 py-1 text-left text-[11px] hover:bg-gray-100 cursor-pointer"
                    >
                      Move to Booking
                    </button>
                    <button
                      onClick={() => setChangingDocTypeId(doc.id)}
                      className="w-full px-3 py-1 text-left text-[11px] hover:bg-gray-100 cursor-pointer"
                    >
                      Change type
                    </button>
                    <button
                      onClick={() => {
                        setDocToDelete({ id: doc.id, type: docType })
                        setMenuDocId(null)
                        setChangingDocTypeId(null)
                      }}
                      className="w-full px-3 py-1 text-left text-[11px] text-red-600 hover:bg-gray-100 cursor-pointer"
                    >
                      Delete
                    </button>
                  </>
                )}
              </div>
              )}
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white overflow-visible">
      {/* Header */}
      <div className="rounded-t-lg border-b border-blue-100 bg-blue-50 px-4 py-2.5 flex items-center gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <span
            className={`truncate font-mono text-[13px] font-semibold text-blue-600 ${isTransferred ? '' : 'cursor-pointer hover:underline'}`}
            onClick={() => {
              if (!isTransferred) onEdit()
            }}
          >
            {booking.internal_reference || carrierLabel}
          </span>
        </div>
        <div className="relative" ref={statusMenuRef}>
          {isTransferred ? (
            <span className={`inline-flex items-center gap-1 rounded-lg border border-gray-300 px-2 py-0.5 text-[11px] font-medium ${statusBadge.className}`}>
              {statusBadge.label}
            </span>
          ) : (
            <button
              type="button"
              onClick={() => setShowStatusMenu((open) => !open)}
              className={`inline-flex items-center gap-1 rounded-lg border border-gray-300 px-2 py-0.5 text-[11px] font-medium hover:border-gray-400 cursor-pointer ${statusBadge.className}`}
              aria-label={`Booking status: ${statusBadge.label}`}
              aria-expanded={showStatusMenu}
            >
              <span>{statusBadge.label}</span>
              <ChevronDown className="h-3 w-3" />
            </button>
          )}
          {showStatusMenu && (
            <div className="absolute left-0 z-20 mt-1 w-48 rounded-md border border-gray-200 bg-white py-1 shadow-lg">
              {BOOKING_STATUSES.filter(option => option.value && option.value !== 'transferred').map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleStatusChange(option.value)}
                  className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-[12px] text-gray-700 hover:bg-gray-50 cursor-pointer"
                >
                  <span>{option.label}</span>
                  {option.value === booking.status && <Check className="h-3.5 w-3.5 text-blue-600" />}
                </button>
              ))}
            </div>
          )}
        </div>
        {quoteExpired && (
          <span className="text-[11px] font-medium px-2 py-0.5 rounded-lg bg-gray-100 text-gray-600">
            Expired
          </span>
        )}
        <div className="flex-1" />
        <div className="flex items-center gap-2">
          {!isTransferred && (
            <>
              <button
                onClick={onEdit}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
                title="Edit booking"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
              {canMoveBooking && (
                <button
                  onClick={openTransferDialog}
                  className="text-gray-400 hover:text-blue-600 cursor-pointer"
                  title="Move booking"
                >
                  <ShipIcon className="h-3.5 w-3.5" />
                </button>
              )}
              <button
                onClick={() => setShowDeleteDialog(true)}
                className="text-gray-300 hover:text-red-600 cursor-pointer"
                title="Cancel booking"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </>
          )}
        </div>
      </div>

      {booking.stage && !booking.stage.terminal && booking.stage.stages.length > 0 && (
        <div className="border-b border-gray-200 px-4 pb-3 pt-3">
          <StageRail stages={booking.stage.stages} size="compact" />
        </div>
      )}

      <div className="grid gap-3 border-b border-gray-200 px-4 py-2 sm:grid-cols-2 lg:grid-cols-5">
        <BookingFact label="Carrier" value={carrierLabel} />
        <BookingFact label="Carrier ref" value={booking.carrier_booking_id || '-'} mono />
        <BookingFact label="Containers" value={containerSummary} />
        <BookingFact label="Sailing" value={booking.sailing_date ? formatDate(booking.sailing_date) : '-'} />
        <BookingFact label="ETA" value={booking.eta ? formatDate(booking.eta) : '-'} />
        {booking.transferred_to_shipment && (
          <BookingFact
            label="Transferred to"
            value={
              <Link
                href={booking.transferred_to_shipment.show_url}
                className="text-blue-600 hover:underline cursor-pointer"
              >
                {booking.transferred_to_shipment.internal_reference}
              </Link>
            }
          />
        )}
        {booking.transferred_from_shipment && (
          <BookingFact
            label="Transferred from"
            value={
              <Link
                href={booking.transferred_from_shipment.show_url}
                className="text-blue-600 hover:underline cursor-pointer"
              >
                {booking.transferred_from_shipment.internal_reference}
              </Link>
            }
          />
        )}
      </div>

      <div className={`border-b px-4 py-2 ${hasPendingWork ? 'border-amber-200 bg-amber-100' : 'border-gray-200 bg-gray-50/60'}`}>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className={`text-[11px] font-semibold uppercase ${hasPendingWork ? 'text-amber-900' : 'text-muted-foreground'}`}>Next work</span>
          {nextWorkItems.map((item) => (
            <span key={item.label} className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${bookingWorkItemClassName(item.tone)}`}>
              {item.label}
            </span>
          ))}
        </div>
      </div>

      <div className="grid gap-4 p-4 lg:grid-cols-[minmax(0,1fr)_260px]">
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-1.5">
              <FileText className="h-3.5 w-3.5 text-muted-foreground" />
              <h4 className="text-[13px] font-semibold text-foreground">Documents</h4>
              <span className="text-[11px] text-muted-foreground">{booking.documents.length}</span>
            </div>
            {!isTransferred && (
              <div className="flex items-center gap-3">
                <button type="button" onClick={() => setShowUploadModal(true)} className="text-[11px] font-medium text-blue-600 hover:underline cursor-pointer">Upload Document</button>
              </div>
            )}
          </div>
          {bookingDocumentRows.length === 0 ? (
            <div className="rounded-md border border-dashed border-gray-200 px-3 py-3 text-[12px] text-muted-foreground italic">
              No documents yet
            </div>
          ) : (
            <div className="divide-y rounded-md border border-gray-200">
              {bookingDocumentRows.map(({ doc, docType }) => renderBookingDocumentRow(doc, docType))}
              {hiddenDocumentCount > 0 && (
                <button
                  type="button"
                  onClick={() => onSelectDocuments(booking.id)}
                  className="block w-full px-3 py-2 text-left text-[12px] font-medium text-blue-600 hover:bg-blue-50 cursor-pointer"
                >
                  + {hiddenDocumentCount} more {hiddenDocumentCount === 1 ? 'document' : 'documents'}
                </button>
              )}
            </div>
          )}
        </div>

        <div className="space-y-3 border-t border-gray-200 pt-4 lg:border-l lg:border-t-0 lg:pl-4 lg:pt-0">
          <details className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
            <summary className="flex list-none cursor-pointer items-center justify-between gap-3 text-[12px] font-semibold text-foreground">
              <span>Commercial</span>
              <span className="font-mono">{booking.total_sell != null ? formatCurrency(booking.total_sell) : '-'}</span>
            </summary>
            <div className="mt-2 space-y-1">
              {booking.quote_snapshot?.rate_validity && (
                <CompactDataRow label="Rate validity" value={booking.quote_snapshot.rate_validity} subdued />
              )}
              {booking.quote_snapshot?.offer_valid_until && (
                <CompactDataRow label="Carrier offer open until" value={formatDate(booking.quote_snapshot.offer_valid_until)} subdued />
              )}
              {quoteSourceLabel && (
                <CompactDataRow label="Rate source" value={quoteSourceLabel} subdued />
              )}
              {quoteCarrierReference && (
                <CompactDataRow label="Quote ref" value={quoteCarrierReference} subdued />
              )}
              {quoteExpired && (
                <CompactDataRow label="Quote state" value="Expired" subdued />
              )}
              {quoteNeedsReview && (
                <CompactDataRow
                  label="Rate review"
                  value={booking.quote_snapshot?.review_reason || 'Manual review required'}
                  subdued
                />
              )}
              <CompactDataRow label="Sell Rate" value={booking.sell_rate != null ? `${formatCurrency(booking.sell_rate)}/ctr` : '-'} />
              <CompactDataRow label="Buy Rate" value={booking.buy_rate != null ? `${formatCurrency(booking.buy_rate)}/ctr` : '-'} indent subdued />
              <CompactDataRow label="Markup" value={booking.markup != null ? `${formatCurrency(booking.markup)}/ctr` : '-'} indent subdued />
              <CompactDataRow label="Shipment" value={booking.buy_shipment != null ? formatCurrency(booking.buy_shipment) : '-'} />
              <CompactDataRow
                label={`Total${booking.container_count ? ` (${booking.container_count} ctr)` : ''}`}
                value={booking.total_sell != null ? formatCurrency(booking.total_sell) : '-'}
                highlight
              />
            </div>
          </details>
        </div>
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel booking?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                This will keep the booking and linked documents visible, and set its status to Cancelled.
              </p>
              {hasMultipleConfirmedBookings && (
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mt-2">
                  <p className="text-amber-800 text-[13px] font-medium">
                    This shipment has {confirmedBookingsCount} confirmed bookings
                  </p>
                  <p className="text-amber-700 text-[12px] mt-1">
                    Cancelling this booking will not affect the other {confirmedBookingsCount - 1} confirmed booking{confirmedBookingsCount > 2 ? 's' : ''}.
                  </p>
                </div>
              )}
              <p className="text-muted-foreground text-[12px] mt-2">You can restore it later by selecting another status.</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowDeleteDialog(false)} className="cursor-pointer">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 cursor-pointer">Cancel booking</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={showTransferDialog} onOpenChange={setShowTransferDialog}>
        <DialogContent className="grid max-h-[calc(100vh-2rem)] sm:max-w-5xl grid-rows-[auto_minmax(0,1fr)_auto]">
          <DialogHeader>
            <DialogTitle>Move booking</DialogTitle>
            <DialogDescription>
              Select a target shipment, review differences, then move this booking context to a new booking row.
            </DialogDescription>
          </DialogHeader>
          <div data-testid="booking-transfer-dialog-body" className="min-h-0 space-y-4 overflow-y-auto pr-1">
            <div className="space-y-1.5">
              <label className="text-[12px] font-semibold text-muted-foreground">Target shipment</label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={transferTargetSearch}
                  onChange={(event) => {
                    setTransferTargetSearch(event.target.value)
                    setTransferTargetShipment(null)
                    setTransferPreview(null)
                    setTransferSubmitError(null)
                  }}
                  className="h-9 pl-8 text-[13px]"
                  placeholder="Search shipments..."
                />
              </div>
              {transferSearchResults.length > 0 && (
                <div className="mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-gray-200 bg-white p-1 shadow-lg">
                  {transferSearchResults.map((result) => (
                    <button
                      key={result.id}
                      type="button"
                      onClick={() => selectTransferTarget(result)}
                      className="flex w-full flex-col rounded px-2 py-2 text-left text-[12px] hover:bg-blue-50 cursor-pointer"
                    >
                      <span className="font-mono font-semibold text-foreground">{result.internal_reference}</span>
                      <span className="text-muted-foreground">
                        {result.client_name || 'No client'} · {valueOrDash(result.origin_port)} → {valueOrDash(result.destination_port)}
                        {result.commodity ? ` · ${result.commodity}` : ''}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {transferSearchLoading && <p className="text-[11px] text-muted-foreground">Searching shipments...</p>}
              {transferLookupError && <p className="text-[12px] text-red-600">{transferLookupError}</p>}
              {transferTargetShipment && (
                <p className="text-[11px] text-muted-foreground">
                  Selected <span className="font-mono font-medium text-foreground">{transferTargetShipment.internal_reference}</span>
                  {transferTargetShipment.client_name ? ` · ${transferTargetShipment.client_name}` : ' · No client assigned'}
                  {transferPreview && transferPreviewLoading && <span> · Updating preview...</span>}
                </p>
              )}
            </div>

            {transferPreviewLoading && !transferPreview && (
              <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-[12px] text-muted-foreground">
                Previewing move...
              </div>
            )}

            {transferPreview && transferPreview.comparison_rows.length > 0 && (
              <div className="overflow-hidden rounded-md border border-gray-200 bg-white text-[12px]">
                <div className="hidden border-b border-gray-200 bg-gray-50 px-3 py-2 text-[11px] font-semibold uppercase text-muted-foreground sm:grid sm:grid-cols-[140px_minmax(0,1fr)_minmax(0,1fr)] sm:gap-3">
                  <span>Detail</span>
                  <span>Source</span>
                  <span>Target</span>
                </div>
                <div className="divide-y divide-gray-100">
                  {transferPreview.comparison_rows.map((row) => {
                    const needsReview = row.mismatch || transferWarningFields.has(row.field)

                    return (
                      <div
                        key={row.field}
                        className={`grid gap-1 px-3 py-2 sm:grid-cols-[140px_minmax(0,1fr)_minmax(0,1fr)] sm:gap-3 ${needsReview ? 'bg-amber-50 text-amber-900' : 'bg-white'}`}
                      >
                        <div className="font-semibold text-foreground">{row.label}</div>
                        <div className="min-w-0 break-words">
                          <span className={`mr-1 sm:hidden ${needsReview ? 'text-amber-700' : 'text-muted-foreground'}`}>Source:</span>
                          <span className="font-medium text-foreground">{row.source}</span>
                        </div>
                        <div className="min-w-0 break-words">
                          <span className={`mr-1 sm:hidden ${needsReview ? 'text-amber-700' : 'text-muted-foreground'}`}>Target:</span>
                          <span className="font-medium text-foreground">{row.target}</span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {transferPreview && transferPreview.hard_blocks.filter((block) => block.field !== 'include_documents').length > 0 && (
              <div className="space-y-2">
                {transferPreview.hard_blocks.filter((block) => block.field !== 'include_documents').map((block) => (
                  <div key={`${block.field}-${block.label}`} className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700">
                    <span className="font-semibold">{block.label}</span>: {block.message}
                  </div>
                ))}
              </div>
            )}

            {transferPreview && transferPreview.warnings.length > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800">
                Highlighted fields need operator's review.
              </div>
            )}

            {transferPreview?.include_documents_required && transferTargetShipment && (
              <div className="grid gap-2 sm:grid-cols-2">
                <div
                  role="button"
                  tabIndex={0}
                  aria-pressed={transferIncludeDocuments === true}
                  onClick={() => {
                    setTransferIncludeDocuments(true)
                    fetchTransferPreview(transferTargetShipment, true)
                  }}
                  onKeyDown={(event) => handleKeyboardActivation(event, () => {
                    setTransferIncludeDocuments(true)
                    fetchTransferPreview(transferTargetShipment, true)
                  })}
                  className={`flex items-center gap-2 rounded-md border px-3 py-2 text-left text-[12px] cursor-pointer ${transferIncludeDocuments === true ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}
                >
                  <Checkbox checked={transferIncludeDocuments === true} tabIndex={-1} />
                  <span>Include documents</span>
                </div>
                <div
                  role="button"
                  tabIndex={0}
                  aria-pressed={transferIncludeDocuments === false}
                  onClick={() => {
                    setTransferIncludeDocuments(false)
                    fetchTransferPreview(transferTargetShipment, false)
                  }}
                  onKeyDown={(event) => handleKeyboardActivation(event, () => {
                    setTransferIncludeDocuments(false)
                    fetchTransferPreview(transferTargetShipment, false)
                  })}
                  className={`flex items-center gap-2 rounded-md border px-3 py-2 text-left text-[12px] cursor-pointer ${transferIncludeDocuments === false ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}
                >
                  <Checkbox checked={transferIncludeDocuments === false} tabIndex={-1} />
                  <span>Leave documents</span>
                </div>
              </div>
            )}

            {transferPreview && transferPreview.warnings.length > 0 && (
              <div
                role="button"
                tabIndex={0}
                aria-pressed={transferAcknowledged}
                onClick={() => setTransferAcknowledged((checked) => !checked)}
                onKeyDown={(event) => handleKeyboardActivation(event, () => setTransferAcknowledged((checked) => !checked))}
                className={`flex items-start gap-2 rounded-md border px-3 py-2 text-left text-[12px] cursor-pointer ${transferAcknowledged ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}
              >
                <Checkbox checked={transferAcknowledged} tabIndex={-1} />
                <span>I reviewed these differences and still want to move this booking.</span>
              </div>
            )}

            {transferSubmitError && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700">
                {transferSubmitError}
              </div>
            )}

          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTransferDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleTransfer} disabled={isTransferring || !transferCanSubmit}>
              {isTransferring ? 'Moving...' : 'Move booking'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Delete Confirmation Modal */}
      <AlertDialog open={!!docToDelete} onOpenChange={(open) => !open && setDocToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete document?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the {docToDelete ? documentTypeLabel(docToDelete.type) : 'document'} document.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDocToDelete(null)} className="cursor-pointer">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteDocument} className="bg-red-600 hover:bg-red-700 cursor-pointer">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {showUploadModal && (
        <DocumentUploadDialog
          open={showUploadModal}
          onOpenChange={setShowUploadModal}
          shipmentId={shipmentId}
          bookings={allBookings}
          pathFor={pathFor}
          initialDocumentType="si"
          initialBookingId={booking.id}
          lockBooking
        />
      )}
    </div>
  )
}

function BookingFact({ label, value, mono }: { label: string; value: ReactNode; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <div className="mb-0.5 text-[10px] font-medium uppercase leading-3 text-muted-foreground">{label}</div>
      <div className={`truncate text-[12px] font-semibold text-foreground ${mono ? 'font-mono' : ''}`}>{value}</div>
    </div>
  )
}

type BookingWorkItem = {
  label: string
  tone: 'amber' | 'blue' | 'gray' | 'green' | 'red'
}

function bookingNextWorkItems(
  booking: BookingFull,
  siDocs: BookingDocument[],
  blDocs: BookingDocument[],
  context: { isConfirmed: boolean; quoteExpired: boolean; quoteNeedsReview: boolean }
): BookingWorkItem[] {
  if (booking.status === 'transferred') return [{ label: 'Transferred', tone: 'gray' }]
  if (booking.status === 'cancelled') return [{ label: 'Cancelled', tone: 'gray' }]

  const documentReviewItems = [
    documentReviewWorkItem('SI', siDocs),
    documentReviewWorkItem('BL', blDocs),
  ].filter((item): item is BookingWorkItem => item !== null)
  const hasSiDocuments = siDocs.length > 0
  const hasBlDocuments = blDocs.length > 0
  const hasDownstreamDocuments = hasSiDocuments || hasBlDocuments

  if (!context.isConfirmed) {
    if (hasDownstreamDocuments) {
      return [{ label: 'Review booking status', tone: 'amber' }, ...documentReviewItems]
    }

    if (booking.status === 'quoted') {
      if (context.quoteExpired) return [{ label: 'Quote expired', tone: 'amber' }]
      if (context.quoteNeedsReview) return [{ label: 'Review rate', tone: 'amber' }]
      return [{ label: 'Follow up quote', tone: 'blue' }]
    }
    if (booking.status === 'accepted') return [{ label: 'Confirm with carrier', tone: 'blue' }]
    return [{ label: 'Await booking confirmation', tone: 'gray' }]
  }

  if (booking.status === 'confirmed') {
    const items: BookingWorkItem[] = []
    if (hasBlDocuments) items.push({ label: 'Review booking status', tone: 'amber' })

    const siReviewItem = documentReviewWorkItem('SI', siDocs)
    const blReviewItem = documentReviewWorkItem('BL', blDocs)
    if (siReviewItem) items.push(siReviewItem)
    if (!hasSiDocuments) items.push({ label: 'Missing SI', tone: 'amber' })
    if (blReviewItem) items.push(blReviewItem)

    return items.length > 0 ? items : [{ label: 'No action needed', tone: 'green' }]
  }

  if (booking.status === 'si_submitted') {
    const items: BookingWorkItem[] = []
    const siReviewItem = documentReviewWorkItem('SI', siDocs)
    const blReviewItem = documentReviewWorkItem('BL', blDocs)
    if (siReviewItem) items.push(siReviewItem)
    if (blReviewItem) {
      items.push(blReviewItem)
    } else if (!hasBlDocuments) {
      items.push({ label: 'Missing BL', tone: 'amber' })
    }

    return items.length > 0 ? items : [{ label: 'No action needed', tone: 'green' }]
  }

  return documentReviewItems.length > 0 ? documentReviewItems : [{ label: 'No action needed', tone: 'green' }]
}

function documentReviewWorkItem(label: 'SI' | 'BL', docs: BookingDocument[]): BookingWorkItem | null {
  const latest = docs.find((doc) => doc.is_latest) || docs[0]
  if (!latest) return null
  if (latest.review_outcome === 'unreviewed') return { label: `Review ${label}`, tone: 'amber' }
  if (latest.review_outcome === 'needs_revision') return { label: `Revise ${label}`, tone: 'red' }
  return null
}

function bookingWorkItemClassName(tone: BookingWorkItem['tone']) {
  if (tone === 'amber') return 'border border-amber-300 bg-amber-50 text-amber-800'
  if (tone === 'blue') return 'border border-blue-200 bg-blue-50 text-blue-700'
  if (tone === 'green') return 'border border-green-200 bg-green-50 text-green-700'
  if (tone === 'red') return 'border border-red-200 bg-red-50 text-red-700'
  return 'border border-gray-200 bg-gray-100 text-gray-600'
}

function documentDisplayName(doc: BookingDocument, docType: DocumentType) {
  if (doc.filename) return doc.filename
  if (docType === 'bl') return doc.document_number || doc.document_subtype || 'BL'
  if (docType === 'si') return doc.document_subtype || 'SI'
  return documentTypeLabel(doc.document_type)
}

// --- Compact Data Row ---

function CompactDataRow({ label, value, highlight, indent, subdued }: { label: string; value: ReactNode; highlight?: boolean; indent?: boolean; subdued?: boolean }) {
  return (
    <div className={`grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-3 leading-4 ${indent ? 'pl-3' : ''}`}>
      <span className={`truncate text-[11px] ${highlight ? 'font-semibold text-foreground' : subdued ? 'text-gray-400' : 'text-muted-foreground'}`}>{label}</span>
      <span className={`max-w-32 truncate text-right font-mono text-[11px] ${highlight ? 'font-bold text-foreground text-[12px]' : subdued ? 'text-gray-400' : 'text-foreground'}`}>{value}</span>
    </div>
  )
}

function rateSourceLabel(source?: string | null) {
  if (source === 'api') return 'API rate'
  if (source === 'manual') return 'Manual rate'
  return null
}

function carrierSnapshotReference(snapshot: BookingFull['quote_snapshot']) {
  if (!snapshot) return null
  return snapshot.carrier_quotation_number || snapshot.carrier_offer_id || snapshot.carrier_price_request_id
}

function valueOrDash(value?: string | null) {
  return value || '-'
}
